import * as React from 'react';
import { t } from '@citrite/translate';
import { WithModalsProps } from '@citrite/web-ui-component';
import {
	ContextMenuItem,
	SkeletonLoaderShapes,
} from '@citrite/workspace-ui-platform-react';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import { trackActionAnalytics } from 'App/Activity/ResourceActionHandler';
import { Session, useResourceManagerContext } from 'App/Activity/ResourceManagerContext';
import { useResourceActions } from 'App/Activity/useResourceActions';
import { useResourceState } from 'App/Activity/useResourceState';
import { desktopMenuActionsIcons, moreOptionsBuilder } from 'App/Tile/contextMenuBuilder';
import { ResourceMenuAction } from 'App/Tile/ResourceMenuAction';
import { useBrowserExtensionPrompt } from 'App/Tile/useBrowserExtensionPrompt';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { EventSite } from 'Workspace/TelemetryEvents/TelemetryEventTypes';
import { useUserContext } from 'Workspace/UserContext';

interface UseResourceContextMenuBuilderParams extends WithModalsProps {
	resource: Resource;
	favoriteRef: React.RefObject<HTMLInputElement>;
}

export const useResourceContextMenuBuilder = ({
	resource,
	favoriteRef,
	showModal,
}: UseResourceContextMenuBuilderParams): ContextMenuItem[] => {
	const { value: resourceContext } = useLoadableResourceContext();
	const { workspaceConfiguration } = useConfigurationContext();
	const { userDetails } = useUserContext();
	const { onDesktopAction } = useResourceManagerContext();
	const canShowBrowserExtensionPrompt = useBrowserExtensionPrompt();
	const hasSubscriptionsEnabled = resourceContext.subscriptionsEnabled();

	const resourceState = useResourceState(resource);
	const resourceActions = useResourceActions(resource);

	const contextMenu = React.useMemo((): ContextMenuItem[] => {
		const menu = moreOptionsBuilder({
			resource,
			resourceContext,
			workspaceConfiguration,
			hasSubscriptionsEnabled,
			favoriteRef,
			userDetails,
			canShowBrowserExtensionPrompt,
			showModal,
		});

		if (resourceState?.state === PowerState.LOADING) {
			const { LoadingBar } = SkeletonLoaderShapes;
			menu.push({
				onClick: () => {},
				children: <LoadingBar width="100%" />,
			});
			return menu;
		}

		if (resourceActions.actions.length > 0) {
			const session: Session = {
				...resourceActions.session,
				applications: [
					{
						id: resource.id,
						resource,
						name: resource.name,
						iconUrl: resource.iconurl,
					},
				],
				machineData: resourceActions.machine,
			} as Session;

			resourceActions.actions.forEach(action => {
				menu.push({
					onClick: () => {
						onDesktopAction(action, session);
						trackActionAnalytics(action, EventSite.Tile);
					},
					children: (
						<ResourceMenuAction icon={desktopMenuActionsIcons[action]}>
							{t(`Workspace:activity_manager.${action}`)}
						</ResourceMenuAction>
					),
				});
			});
		}

		return menu;
	}, [
		resource,
		resourceContext,
		workspaceConfiguration,
		hasSubscriptionsEnabled,
		favoriteRef,
		userDetails,
		canShowBrowserExtensionPrompt,
		showModal,
		resourceState,
		resourceActions,
		onDesktopAction,
	]);

	return contextMenu;
};
